<script setup lang="ts">
import { computed } from 'vue';

import { useTranslations } from '@/shared/composables/useTranslations';
import { formattedPrice } from '@/shared/utils/shopify/money-formatter';

import ThresholdDot from './ThresholdDot.vue';

type Props = {
  shippingThreshold?: number; // in cents (optional, for dual mode)
  giftThreshold?: number; // in cents (optional, for dual mode)
  shippingProgress: number; // percentage (0-100)
  giftProgress?: number; // percentage (0-100, optional, for dual mode)
  currentAmount: number; // current cart amount in cents
  dualModeProgress?: number; // calculated progress for dual mode (0-100)
};

const props = defineProps<Props>();

const { t } = useTranslations();

// Determine if we're in dual mode
const isDualMode = computed(
  () =>
    props.shippingThreshold !== undefined && props.giftThreshold !== undefined
);

// Determine which progress to use for the bar fill
const progressToUse = computed(() => {
  if (isDualMode.value && props.dualModeProgress !== undefined) {
    return Math.min(props.dualModeProgress, 100);
  }
  return Math.min(props.shippingProgress, 100);
});

// Check if thresholds are achieved
const freeShippingAchieved = computed(() => props.shippingProgress >= 100);
const freeGiftAchieved = computed(() => {
  if (props.giftProgress === undefined) {
    return false;
  }
  return props.giftProgress >= 100;
});

// Format threshold amounts
const formattedShippingThreshold = computed(() => {
  if (props.shippingThreshold === undefined) {
    return '';
  }
  return formattedPrice(props.shippingThreshold);
});

const formattedGiftThreshold = computed(() => {
  if (props.giftThreshold === undefined) {
    return '';
  }
  return formattedPrice(props.giftThreshold);
});
</script>

<template>
  <div class="relative mt-2 w-full">
    <div class="relative h-1.5 w-[calc(100%-19px)] rounded-l-md bg-primary-200">
      <div
        class="absolute left-0 h-1.5 rounded-md bg-primary"
        :style="{ width: `${progressToUse}%` }"
      ></div>
    </div>

    <span v-if="isDualMode">
      <ThresholdDot position="middle" :achieved="freeShippingAchieved" />
      <ThresholdDot position="right" :achieved="freeGiftAchieved" />
    </span>
  </div>

  <!-- Dual mode threshold labels -->
  <div
    v-if="isDualMode"
    class="flex w-full justify-between pt-1 text-primary-900"
  >
    <!-- Shipping threshold label (at 50%) -->
    <div
      class="flex flex-col items-center"
      style="margin-left: 50%; transform: translateX(-50%)"
    >
      <span
        :class="[
          'text-sm font-bold',
          freeShippingAchieved ? 'text-primary' : ''
        ]"
      >
        {{ formattedShippingThreshold }}
      </span>
      <span class="text-nowrap text-xs">{{
        t('shipping.free_shipping_label')
      }}</span>
    </div>

    <!-- Gift threshold label (at 100%) -->
    <div class="flex flex-col items-end">
      <span
        :class="['text-sm font-bold', freeGiftAchieved ? 'text-primary' : '']"
      >
        {{ formattedGiftThreshold }}
      </span>
      <span class="text-right text-xs">{{
        t('free_gift.gift_of_your_choice')
      }}</span>
    </div>
  </div>
</template>
